import React from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Field } from 'react-final-form';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from 'components/ToggleButtonGroup';
import { getCardMediums } from 'lib/product';
import { canShowDesignOptions, isOpenLoopCard, isMultichoiceCard, canShowDesignOptionsByProduct, canCustomiseDesign } from '../configurerFlags';
import ProductDesignOptions from 'components/Product/ProductDesignOptions';
import FormSection from 'components/Layout/FormSection';
import MultiCardDisplay from './stepComponents/MultiCardDisplay';
import { CardConfigurerStepProps } from '../CardConfigurerForm';
import { CARD_MEDIUM_PHYSICAL, CARD_MEDIUM_DIGITAL } from 'utilities/constants';
import { useLocation, matchPath } from 'react-router-dom';
import HelperIcon from 'components/Helpers/HelperIcon';
import Typography from '@mui/material/Typography';
import get from 'lodash/get';
import PanelLayout from './stepComponents/PanelLayout';

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      '&[hidden]': {
        display: 'none',
      },
    },
    info: {
      '& .MuiTypography-root': {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(1),
      },
    },
  }),
  { name: 'CardType' },
);

const CardType: React.FC<CardConfigurerStepProps> = ({ step, index, product, form, handleSubmit, values, initialValues, submitButton }) => {
  const classes = useStyles();
  const location = useLocation();

  let cardMediums = getCardMediums(product);
  const routePath = '/redeem/product/:productHandle/:action?';
  const fromRedemption = matchPath(location.pathname + location.hash, routePath);

  const physicalMedium = cardMediums.filter((item) => item.value === CARD_MEDIUM_PHYSICAL);

  //for redemption of multichoice card, they only can redeem and select card medium = ‘physical’ only
  if (isMultichoiceCard(product) && fromRedemption) {
    cardMediums = physicalMedium;
  }

  let defaultMedium = get(cardMediums, '[0].value', null);
  //For open loop card by default it should be physical
  if (isOpenLoopCard(product) && physicalMedium.length) {
    defaultMedium = CARD_MEDIUM_PHYSICAL;
  }

  const cardImageContent = <MultiCardDisplay defaultProductHandle={product.handle} hidePrice={true} values={values} />;

  const formContent = (
    <Stack spacing={4}>
      {cardMediums && cardMediums.length > 0 && (
        <FormSection sx={{ alignItems: { md: 'flex-start' } }}>
          <Field name="cardMedium" defaultValue={defaultMedium}>
            {({ input: { value, onChange } }) => (
              <ToggleButtonGroup
                fullWidth={false}
                name="cardMedium"
                items={cardMediums}
                value={value}
                onChange={(event, newValue) => {
                  newValue ? onChange(newValue) : onChange(value);
                  canCustomiseDesign(product) ? form.change('cardDesign', 'custom') : form.change('cardDesign', 'predesign');
                  if (newValue === CARD_MEDIUM_DIGITAL && isOpenLoopCard(product) && canShowDesignOptionsByProduct(product)) {
                    // @ts-ignore
                    form.change('cardDesignHandle', null);
                  }
                }}
              />
            )}
          </Field>
        </FormSection>
      )}
      {cardMediums && cardMediums.length === 0 && (
        <Stack direction={'row'} alignItems={'center'} spacing={1} marginTop={2} className={classes.info}>
          <Typography color="secondary" variant="subtitle1">
            This card is currently unavailable for purchase.
          </Typography>
          <HelperIcon message="Please contact customer support if you believe this card should be available." color="secondary" />
        </Stack>
      )}
      {fromRedemption && (
        <Stack direction={'row'} alignItems={'center'} spacing={1} marginTop={2} className={classes.info}>
          <Typography color="secondary" variant="subtitle1">
            {values.cardMedium === CARD_MEDIUM_PHYSICAL ? 'Sent to single mailing address at checkout' : 'Receive an e-gift or print at home'}
          </Typography>
          <HelperIcon message="TBA" color="secondary" />
        </Stack>
      )}
      {canShowDesignOptions(product, values) && <Field name="cardDesign">{({ input }) => <ProductDesignOptions product={product} form={form} input={input} next={handleSubmit} />}</Field>}
    </Stack>
  );

  return (
    <div className={classes.root} hidden={step !== index}>
      <PanelLayout value={step} index={index} title="Select your card type" cardImageContent={cardImageContent} formContent={formContent} buttonsContent={submitButton} />
    </div>
  );
};

export default CardType;
